// Guitar
// Features:
// - Static guitar mesh with invisible physics body for interaction
// - Two sounds that play alternating riffs on click

import { useEffect, useRef } from 'react'
import { useThree } from '@react-three/fiber'
import { PositionalAudio } from '@react-three/drei'
import { RigidBody } from '@react-three/rapier'
import * as THREE from 'three'

import { useStore } from '../../store/useStore'

export default function Guitar({ nodes, material, physicsMaterial }) {
    const pos1Ref = useRef(null)
    const pos2Ref = useRef(null)
    const { camera } = useThree()

    const isSoundOn = useStore((state) => state.isSoundOn)

    // Initialization: Audio Listener & Volume config
    useEffect(() => {
        // Ensure listener
        if (!camera.children.some((c) => c.type === 'AudioListener')) {
            camera.add(new THREE.AudioListener())
        }

        // Configure both audio sources
        const configureAudio = (audio) => {
            if (!audio) return
            audio.setRefDistance(2.0)
            audio.setRolloffFactor(2.5)
            audio.setDistanceModel('exponential')
            audio.setLoop(false)
            audio.setVolume(isSoundOn ? 0.15 : 0)
        }

        configureAudio(pos1Ref.current)
        configureAudio(pos2Ref.current)
    }, [camera, isSoundOn])

    // Interaction: Play random riff
    useEffect(() => {
        const onMouseDown = (e) => {
            if (e.button !== 0) return
            const state = useStore.getState()

            if (state.currentHitName === 'Guitar' && state.isPointerLocked) {
                if (!state.isSoundOn) return

                const audioNode =
                    Math.random() < 0.5 ? pos1Ref.current : pos2Ref.current
                if (!audioNode) return

                // Resume AudioContext if needed (handling user gesture requirement)
                const ctx = THREE.AudioContext.getContext()
                if (ctx && ctx.state !== 'running') ctx.resume()

                if (audioNode.isPlaying) audioNode.stop()
                audioNode.play()
            }
        }
        window.addEventListener('mousedown', onMouseDown)
        return () => window.removeEventListener('mousedown', onMouseDown)
    }, [])

    return (
        <group>
            <RigidBody
                colliders="trimesh"
                type="fixed"
                userData={{ name: 'Guitar' }}
                position={[-2.758, 0.784, 0.904]}
            >
                <mesh
                    geometry={nodes.guitar_physics.geometry}
                    material={physicsMaterial}
                />
            </RigidBody>

            <mesh
                geometry={nodes.guitar.geometry}
                material={material}
                position={[-2.758, 0.784, 0.904]}
                rotation={[0.025, -0.438, -1.299]}
            />

            <PositionalAudio
                ref={pos1Ref}
                url="/sounds/guitar_01.mp3"
                autoplay={false}
                loop={false}
                position={[-2.758, 0.784, 0.904]}
            />
            <PositionalAudio
                ref={pos2Ref}
                url="/sounds/guitar_02.mp3"
                autoplay={false}
                loop={false}
                position={[-2.758, 0.784, 0.904]}
            />
        </group>
    )
}
